import { Children, FC, PropsWithChildren, useEffect, useRef, useState } from "react";

interface SyncInfiniteScrollProps {
    allData: any[];
    pageSize: number;
}

const DEBUG_MODE = false;

const SyncInfiniteScroll: FC<PropsWithChildren<SyncInfiniteScrollProps>> = ({ children, pageSize, allData }) => {
    const endItem = useRef<HTMLDivElement>(null);
    const doneRef = useRef<boolean>(false);
    const observer = useRef<IntersectionObserver>(undefined);

    const [endIdx, setEndIdx] = useState<number>(Children.toArray(children).slice(0, pageSize).length);

    useEffect(() => {
        if (!endItem.current) {
            return undefined;
        }

        observer.current = new IntersectionObserver((entries, observerRef) => {
            if (doneRef.current) {
                observerRef.takeRecords();
                return;
            }

            if (entries[entries.length - 1].isIntersecting) {
                setEndIdx((prev) => {
                    let nextEnd = prev + pageSize;

                    if (nextEnd > allData.length) {
                        nextEnd = allData.length;
                        doneRef.current = true;
                    }

                    return nextEnd;
                });
            }
        });

        observer.current.observe(endItem.current);

        return () => {
            observer.current?.takeRecords();
            observer.current?.disconnect();
            doneRef.current = false;
        };
    }, [allData, endIdx]);

    return (
        <>
            {Children.toArray(children).slice(0, endIdx)}
            <div
                style={{
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    height: allData.length !== endIdx ? "20px" : "unset",
                    alignItems: "center",
                    ...(DEBUG_MODE ? { border: "2px solid red" } : {}),
                }}
            >
                <div
                    ref={endItem}
                    style={{
                        position: "absolute",
                        height: "60px",
                        width: "100%",
                        top: "-60px",
                        ...(DEBUG_MODE ? { border: "2px solid green" } : { visibility: "hidden" }),
                    }}
                />
            </div>
        </>
    );
};

export default SyncInfiniteScroll;
